import React from 'react';
import styled from 'styled-components';

import UnstyledLink from '#/components/basic/Link/UnstyledLink';
import Tooltip from '#/components/basic/tooltip/Tooltip';
import {
  formatSessionDateOrCountdown,
  getSessionStatusBackground,
  getSessionStatusColorVar,
  MarketSessionDisplayStatus,
  MarketSessionInfo,
} from '#/components/markets/intermittent/intermittentMarkets';
import { MARKET_CLOSES_PERIODICALLY_LINK_TOOLTIP } from '#/components/markets/intermittent/intermittentMarketsCopy';
import { useIntermittentMarketSessionDisplay } from '#/components/markets/intermittent/useIntermittentMarketSessionDisplay';
import { urls } from '#/config/constants';
import { useSelector } from '#/reduxState/storeCore';

const BannerLink = styled(UnstyledLink)`
  display: block;
  text-decoration: none;
`;

const Wrap = styled.div`
  display: flex;
  gap: 1px;
  margin: 8px 0;
  border-radius: 4px;
  overflow: hidden;
`;

const Cell = styled.div<{
  $status: MarketSessionDisplayStatus;
  $transitionVeryClose?: boolean;
}>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 8px;
  font-size: var(--small);
  line-height: 16px;
  white-space: nowrap;
  color: ${({ $status, $transitionVeryClose }) =>
    getSessionStatusColorVar($status, $transitionVeryClose)};
  background: ${({ $status, $transitionVeryClose }) =>
    getSessionStatusBackground($status, $transitionVeryClose)};
`;

interface Props {
  sessionInfo?: MarketSessionInfo;
}

/**
 * Order ticket session state banner shown above the Buy/Sell buttons for
 * intermittent markets: "Status: Open | Next Close: 59:23" — the time is a
 * mm:ss countdown within 60 min of the transition, the local "HH:mm(+d)" time
 * earlier. Green/red, switching to yellow within 5 min of the transition
 * (`transitionVeryClose`). Once the API-indicated transition time is reached,
 * "Close Pending" / "Open Pending" is shown until the engine confirms the new
 * session state. On mobile a more concise wording is used: "Open | Closes: 14:00".
 * The whole banner links to the docs in a new tab and shows a hover tip
 * on desktop only.
 */
export default function MarketSessionBanner({ sessionInfo }: Props) {
  const isMobile = useSelector(state => state.screen.isMobile);
  const session = useIntermittentMarketSessionDisplay(
    sessionInfo,
    'every-tick'
  );
  if (!session) {
    return null;
  }
  const {
    status,
    transitionVeryClose,
    isClosedForTrading,
    nextTransitionMs,
    nowMs,
  } = session;

  return (
    <Tooltip
      text={isMobile ? undefined : MARKET_CLOSES_PERIODICALLY_LINK_TOOLTIP}
      maxWidthPx={300}
      wrapper="div"
      pointer
      fixedPosition
    >
      <BannerLink href={urls.intermittentMarketsDocs}>
        <Wrap>
          <Cell $status={status} $transitionVeryClose={transitionVeryClose}>
            {isMobile ? '' : 'Status: '}
            {isClosedForTrading ? 'Closed' : 'Open'}
          </Cell>
          <Cell $status={status} $transitionVeryClose={transitionVeryClose}>
            {status === 'opening'
              ? 'Open Pending'
              : status === 'closing'
              ? 'Close Pending'
              : isClosedForTrading
              ? `${
                  isMobile ? 'Opens' : 'Next Open'
                }: ${formatSessionDateOrCountdown(nextTransitionMs, nowMs)}`
              : `${
                  isMobile ? 'Closes' : 'Next Close'
                }: ${formatSessionDateOrCountdown(nextTransitionMs, nowMs)}`}
          </Cell>
        </Wrap>
      </BannerLink>
    </Tooltip>
  );
}
